// Keep the original key so existing project-only visits remain compatible.
export const TOWN_CONTINUATION_KEY = 'pixel-town:last-project';
const VERSION = 2;
const SAFE_ID = /^[A-Za-z0-9_-]+$/;
const EPISODE_ROUTE = /^\/project\/([A-Za-z0-9_-]+)\/episode\/([A-Za-z0-9_-]+)\/([a-z0-9-]+)$/;

// Only destinations exposed by the town's episode rooms can be resumed.
const TOOL_LABELS = Object.freeze({
  upload: '本集剧本',
  'mini-storyboard': '小故事板',
  'storyboard-board': '故事板导演台',
  storyboard: '分镜编辑器',
  'master-shot': '主镜参考',
  canvas: '画布模式',
  'motion-storyboard': '动态分镜',
  'rough-cut': '粗剪台',
  orbit: '3D 视角',
  imageagent: '图片生成',
  videoagent: '视频生成',
  musicagent: '音乐音频',
  resources: '资源中心',
  character: '角色工坊',
  scene: '场景工坊',
  prop: '道具工坊',
  'ai-writer': '智能改编',
  education: '教学中心',
});

function safeId(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const id = String(value);
  return SAFE_ID.test(id) ? id : '';
}

function textLabel(value, fallback) {
  return typeof value === 'string' && value.trim() ? value.trim().slice(0, 120) : fallback;
}

/** Untrusted storage URLs must match this exact project, episode and tool. */
export function getTownEpisodeTarget(record, projectId = record?.id) {
  if (!record || typeof record.route !== 'string') return null;
  const id = safeId(projectId);
  const episodeId = safeId(record.episodeId);
  const match = record.route.match(EPISODE_ROUTE);
  if (!id || safeId(record.id) !== id || !episodeId || !match
    || match[1] !== id || match[2] !== episodeId
    || !Object.hasOwn(TOOL_LABELS, match[3])) return null;
  return {
    route: `/project/${id}/episode/${episodeId}/${match[3]}`,
    episodeId,
    episodeLabel: textLabel(record.episodeLabel, '本集'),
    toolLabel: TOOL_LABELS[match[3]],
  };
}

/** Pure migration/validation; malformed or future-version records fail closed. */
export function parseTownContinuation(raw) {
  try {
    const record = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!record || Array.isArray(record) || !safeId(record.id)
      || (record.version !== undefined && record.version !== VERSION)) return null;
    return {
      version: VERSION,
      id: safeId(record.id),
      name: textLabel(record.name, '未命名项目'),
      visitedAt: Number.isFinite(record.visitedAt) ? record.visitedAt : 0,
      ...getTownEpisodeTarget(record),
    };
  } catch {
    return null;
  }
}

/** Visiting a floor preserves the last actual workstation in that project. */
export function createTownProjectVisit(previous, project, visitedAt, allowEpisode = true) {
  const id = safeId(project?.id);
  if (!id) return null;
  return {
    version: VERSION,
    id,
    name: textLabel(project.name || project.title, '未命名项目'),
    visitedAt,
    ...(allowEpisode ? getTownEpisodeTarget(previous, id) : null),
  };
}

/** Called from a town room's navigation event, never from a page-load effect. */
export function createTownEpisodeVisit({ projectId, projectName, episodeId, episodeLabel, route }, visitedAt) {
  const project = createTownProjectVisit(null, { id: projectId, name: projectName }, visitedAt);
  const target = getTownEpisodeTarget({ id: projectId, episodeId, episodeLabel, route }, projectId);
  return project && target ? { ...project, ...target } : null;
}

/** The current authenticated list, not storage, determines project access. */
export function resolveTownContinuation(record, projects, canResumeEpisode = () => true) {
  const validRecord = parseTownContinuation(record);
  if (!validRecord || !Array.isArray(projects)) return null;
  const project = projects.find((item) => safeId(item?.id) === validRecord.id);
  if (!project) return null;
  const target = canResumeEpisode(project) ? getTownEpisodeTarget(validRecord, project.id) : null;
  return { project, target };
}

export function getTownContinuationLabel({ project, target }) {
  if (!target) return `继续《${textLabel(project.name || project.title, '未命名项目')}》`;
  const episodeNumber = target.episodeLabel.match(/^第\s*([一二三四五六七八九十百千万零〇两\d]+)\s*集/);
  const episodeLabel = episodeNumber ? `第${episodeNumber[1]}集` : target.episodeLabel;
  return `继续 ${episodeLabel}·${target.toolLabel}`;
}

export function loadTownContinuation() {
  try { return parseTownContinuation(localStorage.getItem(TOWN_CONTINUATION_KEY)); }
  catch { return null; }
}

export function saveTownContinuation(record) {
  if (!record) return;
  try { localStorage.setItem(TOWN_CONTINUATION_KEY, JSON.stringify(record)); }
  catch { /* Storage is optional; navigation still works when it is unavailable. */ }
}
